import { supabase } from './supabase'
import { labsWrite } from './labs'

// ── The ONE episode-reaction write ────────────────────────────────────────────
// set_episode_reaction had grown two independent client implementations with
// DIFFERENT semantics (audit 2026-08-14):
//   • PostWatchSheet's mood step: labsWrite + one retry + pulse-reacted dispatch,
//     but no toggle-off and p_note hardwired to null — re-picking a mood from the
//     post-watch card silently WIPED an existing note.
//   • EpisodePulseSheet's usePulse.react: toggle-off + note-preserving, but no
//     labsWrite wrapper, no retry, and no pulse-reacted dispatch.
// This module is the union of the good halves. Both surfaces call it, so "rating
// an episode" and "the Pulse" are one system, not one and a half.
//
// Args:
//   emoji    — the reaction to set; null explicitly clears.
//   note     — string sets/replaces the note; undefined = preserve the existing one.
//   current  — { emoji, note } when the caller already knows the user's reaction
//              (the Pulse sheet does). undefined = unknown → the lib reads the
//              caller's own row (episode_reactions is select-own-only RLS, so this
//              is allowed and cheap) to honor note-preservation.
//   toggle   — true = re-sending the current emoji (with note undefined) CLEARS it
//              (the Pulse sheet's re-tap). false = idempotent re-set (post-watch
//              card, where a re-tap must never silently delete).
//   stillAlive — retry gate: a delayed retry must not write for an episode the
//              user has since undone or moved past.
//
// nextep:pulse-reacted fires immediately (before the RPC resolves) on any
// non-clearing write, so the Pulse peek is cancelled unconditionally — the
// cancel must not wait on network (contract in EpisodePulseHost.onReacted).
//
// Returns { error, cleared }.
export async function setEpisodeReaction({ showId, season, episode, emoji, note, current, toggle = false, stillAlive = () => true }) {
  const dispatchReacted = () => {
    try {
      window.dispatchEvent(new CustomEvent('nextep:pulse-reacted', {
        detail: { showId, season, episode },
      }))
    } catch { /* noop */ }
  }
  // Peek-cancel must NOT wait on network (contract in EpisodePulseHost.onReacted).
  // With toggle:false, "clearing" doesn't depend on the current row, so dispatch
  // NOW — before the own-row fetch below, which is a network round trip that
  // would otherwise delay the cancel. The toggle:true case dispatches after
  // `clearing` is known, but its only caller (the Pulse sheet) always supplies
  // `current`, so that path never fetches and stays synchronous too.
  if (!toggle && emoji != null) dispatchReacted()
  let cur = current
  if (cur === undefined && note === undefined) {
    // Unknown state and the caller isn't setting a note — read our own row so an
    // existing note survives the emoji change. Failure degrades to null (the old
    // PostWatchSheet behavior), never blocks the write.
    try {
      const { data: session } = await supabase.auth.getSession()
      const uid = session?.session?.user?.id
      if (uid) {
        const { data } = await supabase.from('episode_reactions')
          .select('emoji, note')
          .match({ user_id: uid, show_id: Number(showId), season, episode })
          .maybeSingle()
        cur = data || null
      }
    } catch { cur = null }
  }
  const clearing = emoji == null || (toggle && cur?.emoji === emoji && note === undefined)
  if (toggle && !clearing) dispatchReacted()
  const write = () => labsWrite('react to episode', () => supabase.rpc('set_episode_reaction', {
    p_show: showId, p_season: season, p_episode: episode,
    p_emoji: clearing ? null : emoji,
    p_note: note ?? cur?.note ?? null,
  }))
  let { error } = await write()
  if (error) {
    await new Promise(r => setTimeout(r, 800))
    if (stillAlive()) {
      const retry = await write()
      error = retry?.error || null
    }
  }
  return { error, cleared: clearing }
}

// ── Your own trail ────────────────────────────────────────────────────────────
// The mood you picked was stored and never shown back to you (2026-10-24, Jason: "it
// sticks but falls short of its potential"). These two reads are the trail: every mood
// of yours on a show (the show page badges its rings), and your latest one (the Home
// card's "Last night E4 left you 🤯"). Own rows only; episode_reactions is select-own.
export async function myReactionsForShow(userId, showId) {
  if (!userId || !showId) return {}
  try {
    const { data } = await supabase.from('episode_reactions').select('season, episode, emoji')
      .eq('user_id', userId).eq('show_id', Number(showId)).not('emoji', 'is', null)
    const out = {}
    for (const r of data || []) out[`${r.season}-${r.episode}`] = r.emoji
    return out
  } catch { return {} }
}
export async function myLatestReaction(userId, showId) {
  if (!userId || !showId) return null
  try {
    const { data } = await supabase.from('episode_reactions').select('season, episode, emoji, updated_at, created_at')
      .eq('user_id', userId).eq('show_id', Number(showId)).not('emoji', 'is', null)
      .order('updated_at', { ascending: false, nullsFirst: false }).limit(1).maybeSingle()
    return data || null
  } catch { return null }
}
export { trailLine } from './moods'
